import React, { useState, useEffect, useCallback } from 'react'
import { Plus, FloppyDisk, ChartBar, Buildings, Lightning, Broom, Wrench, CheckCircle, Warning, DownloadSimple, FileXls, ArrowRight } from '@phosphor-icons/react'
import * as XLSX from 'xlsx'
import { supabase } from '../lib/supabase'

// ─── Carga de datos del proyecto desde otras evaluaciones ────────────────────
// Lee ENE 1 (GFA + consumo energético) y MAT 1 (GFA alternativo) almacenados
// en Supabase para pre-rellenar la calculadora con datos reales del proyecto.
async function fetchProjectData(projectId) {
  const result = { gfa: null, energiaAnual: null, projectName: null, fuentes: [] }
  try {
    // ENE 1 — zonas: [{m2, con_eval (kWh/m²·año)}]
    const { data: ene1 } = await supabase.from('evaluations')
      .select('justificacion')
      .eq('project_id', projectId)
      .eq('criteria_code', 'ENE 1')
      .maybeSingle()
    if (ene1?.justificacion) {
      const parsed = JSON.parse(ene1.justificacion)
      const zonas = parsed.ene1_calculadora?.zonas || []
      const totalM2 = zonas.reduce((s, z) => s + (parseFloat(z.m2) || 0), 0)
      // consumo total anual = suma(con_eval × m2) por zona — kWh/año
      const totalConsumo = zonas.reduce((s, z) => {
        const m2 = parseFloat(z.m2) || 0
        const conEval = parseFloat(z.con_eval) || 0
        return s + conEval * m2
      }, 0)
      if (totalM2 > 0) { result.gfa = totalM2; result.fuentes.push('GFA extraído de ENE 1') }
      if (totalConsumo > 0) { result.energiaAnual = totalConsumo; result.fuentes.push(`Consumo energético total: ${Math.round(totalConsumo).toLocaleString('es-ES')} kWh/año (ENE 1)`) }
    }
    // MAT 1 — fallback GFA si ENE 1 no tiene m2
    if (!result.gfa) {
      const { data: mat1 } = await supabase.from('evaluations')
        .select('justificacion')
        .eq('project_id', projectId)
        .eq('criteria_code', 'MAT 1')
        .maybeSingle()
      if (mat1?.justificacion) {
        const parsed = JSON.parse(mat1.justificacion)
        const gfa = parsed.mat1_calculadora?.gfa || parsed.gfa
        if (gfa) { result.gfa = parseFloat(gfa); result.fuentes.push('GFA extraído de MAT 1') }
      }
    }
    // Nombre del proyecto
    const { data: proj } = await supabase.from('projects')
      .select('name').eq('id', projectId).maybeSingle()
    if (proj?.name) result.projectName = proj.name
  } catch (e) { console.error('fetchProjectData error:', e.message) }
  return result
}

// ─── Importador de presupuesto desde biblioteca ──────────────────────────────
async function fetchLibraryBudgetFiles(projectId) {
  const { data } = await supabase.from('project_documents')
    .select('id, file_name, storage_path, uploaded_at')
    .eq('project_id', projectId)
    .order('uploaded_at', { ascending: false })
  return (data || []).filter(d => d.file_name.match(/\.(xlsx|xls|pdf)$/i))
}

// ─── Clasificador automático de partidas presupuestarias ─────────────────────
const KW_TECNICO = [
  // Fontanería y saneamiento
  'fontaner','saneamiento','fontaneria','plomería','plomeria','agua fría','agua caliente',
  'afs','acs','red de agua','suministro de agua','evacuación','colectores','bajantes',
  'tuberías','tuberia','tubería','piscina','riego','grifo','sanitario',
  // Calefacción / climatización / HVAC
  'calefacción','calefaccion','climatización','climatizacion','hvac','frío calor',
  'aire acondicionado','fancoil','fan coil','vrf','vrvs','aerotermia','geotermia',
  'bomba de calor','chiller','uta','utas','ahu','split','radiador','suelo radiante',
  'caldera','caloras','fancoils',
  // Ventilación
  'ventilación','ventilacion','extracción','extraccion','aire primario','recuperador',
  'conductos','climatizador','aire exterior','extractores','ventilador',
  // Electricidad
  'electricidad','eléctric','electric','instalación eléctrica','baja tensión','bt ',
  'media tensión','cuadro eléctrico','cuadro general','subcuadro','ct ','grupo electróg',
  'grupo electrog','ups ','sai ','luminari','iluminacion','iluminación','alumbrado',
  'emergencia','led','pantalla led',
  // Telecomunicaciones
  'telecomunicacion','telecomunicación','voz datos','voz y datos','fibra óptica',
  'fibra optica','rack','cableado','red local','wi-fi','wifi','antena','megafonía',
  'megafonia','cctv','videovigilancia','control acceso','intrusión','intrusion',
  'intercomunicador',
  // Transporte vertical
  'ascensor','elevador','montacargas','escalera mecánica','escalera mecanica',
  'pasillo rodante','salvaescaleras','plataforma elevadora',
  // Domótica / BMS
  'domótica','domotica','bms','automatización','automatizacion','control técnico',
  'control tecnico','scada','kns','knx','inmótica','inmotica',
  // PCI / Protección incendios
  'incendio','pci ','extinción','extincion','sprinkler','rociador','detección',
  'deteccion','centralita de incendio','boca de incendio','bie ','extintores',
  'sectorización','sectorizacion','hidrante',
  // Gas
  'gas natural','instalación de gas','gas lp','gasoil','combustible',
  // Energía solar / FV
  'fotovoltaic','solar','placas solares','captadores','paneles solares','autoconsumo',
  // Residuos / especiales
  'residuos','basura','compactador',
]

const KW_ESTRUCTURAL = [
  // Cimentación
  'cimentación','cimentacion','zapata','pilote','micropilote','losa de cimentación',
  'muro de contención','contencion','pantalla de pilotes','excavación','excavacion',
  'vaciado','movimiento de tierras','tierras','terraplén','terraplen',
  // Estructura
  'estructura','forjado','losa','pilar','viga','jácena','jacena','muro estructural',
  'hormigón','hormigon','acero estructural','metálica','metalica','prefabricado',
  'celosía','celosia','armado','armadura','encofrado','pórtico','portico',
  // Cubierta y cerramiento exterior
  'cubierta','cubie','impermeabilización','impermeabilizacion','fachada','cerramiento',
  'panel sandwich','muro cortina','curtain wall','revest','revestimiento exterior',
  'aislamiento térmico','aislamiento termico','fachada ventilada','fábrica','fabrica',
  'bloque','ladrillo','mortero','enlucido','enfoscado',
  // Tabiquería y particiones
  'tabiquería','tabiqueria','tabique','partición','particion','división','division',
  'pladur','cartón yeso','carton yeso','trasdosado',
  // Acabados interiores
  'pavimento','solado','alicatado','baldosa','porcelanato','parquet','tarima',
  'falso techo','techo suspendido','suelo técnico','suelo tecnico','pintura',
  'yeso','escayola','revestimiento interior','acabado',
  // Carpintería
  'carpintería','carpinteria','ventana','puerta','vidrio','acristalamiento',
  'aluminio','pvc ','madera','persiana','brise soleil','parasol','protección solar',
  'proteccion solar','lama','barandilla','barandal','pasamanos','escalera ',
  // Urbanización
  'urbanización','urbanizacion','pavimentación exterior','pavimentacion exterior',
  'jardinería','jardineria','vial','aparcamiento','parking','cerramiento exterior',
  'verja','muro perimetral','bordillo','acerado','acerado',
]

function clasificarPartida(descripcion) {
  const d = descripcion.toLowerCase()
  const scoreTec = KW_TECNICO.filter(kw => d.includes(kw)).length
  const scoreEst = KW_ESTRUCTURAL.filter(kw => d.includes(kw)).length
  if (scoreTec > 0 && scoreTec >= scoreEst) return 'tecnico'
  if (scoreEst > 0) return 'estructural'
  return ''  // sin clasificar → el usuario decide
}

// Patrón para detectar filas que son totales/subtotales o encabezados de capítulo (no partidas reales)
const RE_TOTAL_EXCEL = /^\s*(total|subtotal|suma\s|presupuesto\s+(general|base|total|ejecuci[oó]n)|importe\s+total|cap[ií]tulo\s*[\d.:]+|cap\.\s*\d|c\d{2}\s*[-–]|resumen\s+cap)/i

async function downloadAndParseExcel(storagePath) {
  const { data: urlData } = await supabase.storage.from('evidences').createSignedUrl(storagePath, 300)
  if (!urlData?.signedUrl) throw new Error('No se pudo generar URL de descarga')
  const response = await fetch(urlData.signedUrl)
  if (!response.ok) throw new Error(`Error al descargar: ${response.statusText}`)
  const arrayBuffer = await response.arrayBuffer()
  const wb = XLSX.read(arrayBuffer, { type: 'array' })

  // Detectar hojas "Resumen" o "Summary" — solo las incluimos si no hay otras hojas de detalle
  const hojasDetalle = wb.SheetNames.filter(n => !/(resumen|summary|portada|indice|índice|cover)/i.test(n))
  const hojasAProcesar = hojasDetalle.length > 0 ? hojasDetalle : wb.SheetNames

  // Escanear hojas buscando filas con descripción + importe
  const partidas = []
  for (const sheetName of hojasAProcesar) {
    const sheet = wb.Sheets[sheetName]
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      // Buscar descripción en las primeras 4 columnas
      let desc = '', importe = null
      for (let c = 0; c < Math.min(row.length, 4); c++) {
        if (typeof row[c] === 'string' && row[c].trim().length > 3) { desc = row[c].trim(); break }
      }
      // Filtrar filas que son totales/subtotales por descripción
      if (!desc || RE_TOTAL_EXCEL.test(desc)) continue

      // Buscar importe numérico en las últimas columnas
      for (let c = Math.min(row.length - 1, 10); c >= 1; c--) {
        const raw = row[c]
        let val = NaN
        if (typeof raw === 'number') { val = raw }
        else if (typeof raw === 'string') {
          // "1.234.567,89" → 1234567.89
          const cleaned = raw.replace(/\./g, '').replace(',', '.')
          val = parseFloat(cleaned)
        }
        if (!isNaN(val) && val > 500) { importe = val; break }
      }
      if (desc && importe && importe > 500) {
        partidas.push({ hoja: sheetName, fila: i + 1, descripcion: desc, importe, asignacion: clasificarPartida(desc) })
      }
    }
  }

  // Deduplicar por descripción+importe exactos (insensible a mayúsculas)
  const vistas = new Set()
  return partidas.filter(p => {
    const key = `${p.descripcion.toLowerCase().trim()}|${p.importe}`
    if (vistas.has(key)) return false
    vistas.add(key)
    return true
  }).slice(0, 200)
}

// ─── Parser PDF presupuesto ──────────────────────────────────────────────────
// Extrae texto de cada página con pdfjs-dist y busca líneas con importe al final
async function downloadAndParsePDF(storagePath) {
  const { data: urlData } = await supabase.storage.from('evidences').createSignedUrl(storagePath, 300)
  if (!urlData?.signedUrl) throw new Error('No se pudo generar URL de descarga')
  const response = await fetch(urlData.signedUrl)
  if (!response.ok) throw new Error(`Error al descargar: ${response.statusText}`)
  const arrayBuffer = await response.arrayBuffer()

  const pdfjsLib = await import('pdfjs-dist')
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}${process.env.PUBLIC_URL}/pdf.worker.min.mjs`

  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
    useSystemFonts: true,
    disableAutoFetch: true,
    disableStream: true,
  }).promise
  const partidas = []

  // Regex para detectar importes con formato español: "1.234,56" o "1234,56" o "1.234.567,89"
  // Captura: texto antes + importe al final de línea (con o sin €)
  const reImporte = /^(.+?)\s+([\d]{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)\s*€?\s*$/

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum)
    const textContent = await page.getTextContent()

    // Reconstruir líneas agrupando items por posición Y (±3px)
    const itemsByY = {}
    for (const item of textContent.items) {
      if (!item.str?.trim()) continue
      const y = Math.round(item.transform[5] / 3) * 3
      if (!itemsByY[y]) itemsByY[y] = []
      itemsByY[y].push({ x: item.transform[4], text: item.str })
    }

    // Ordenar por Y descendente (de arriba abajo en PDF) y reconstruir líneas
    const lines = Object.entries(itemsByY)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([, items]) => items.sort((a, b) => a.x - b.x).map(i => i.text).join(' ').trim())
      .filter(l => l.length > 3)

    for (const line of lines) {
      const match = line.match(reImporte)
      if (!match) continue
      const desc = match[1].trim()
      // Parsear importe: "1.234.567,89" → 1234567.89
      const importeStr = match[2].replace(/\./g, '').replace(',', '.')
      const importe = parseFloat(importeStr)
      if (isNaN(importe) || importe <= 500) continue
      // Filtrar líneas que son totales o subtotales repetidos
      if (/total|subtotal|suma|presupuesto general/i.test(desc) && desc.length < 30) continue
      partidas.push({ hoja: `Pág.${pageNum}`, fila: 0, descripcion: desc, importe, asignacion: clasificarPartida(desc) })
    }
  }

  // Deduplicar
  const vistas = new Set()
  return partidas.filter(p => {
    const key = `${p.descripcion}|${p.importe}`
    if (vistas.has(key)) return false
    vistas.add(key); return true
  }).slice(0, 200)
}

// ─── Dispatcher: Excel o PDF según extensión ─────────────────────────────────
async function downloadAndParseFile(storagePath, fileName) {
  if (fileName.match(/\.pdf$/i)) return downloadAndParsePDF(storagePath)
  return downloadAndParseExcel(storagePath)
}

// ─── Supabase persistence ─────────────────────────────────────────────────────
async function saveData(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Gestión',
      criteria_code: 'GST5-DATA',
      criteria_name: 'GST5 CCV Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
  } catch (e) { console.error('GST5 save error:', e.message) }
}

async function loadData(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'GST5-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

// ─── NPV / VAN helpers ────────────────────────────────────────────────────────
// VAN de una renta anual creciente durante n años
// A = coste anual año 1, r = tasa descuento, p = tasa inflación, n = años
function vanRentaCreciente(A, r, p, n) {
  if (!A || A === 0) return 0
  if (Math.abs(r - p) < 0.0001) {
    // caso límite r ≈ p
    return A * n * Math.pow(1 + r, -1)
  }
  return A * (1 - Math.pow((1 + p) / (1 + r), n)) / (r - p)
}

// VAN de sustituciones de un elemento técnico con vida útil d dentro de período n
// El coste de sustitución crece con inflación de construcción desde año 0
function vanSustituciones(C, d, n, r, pConst) {
  if (!C || C === 0 || !d || d <= 0) return 0
  let van = 0
  let k = 1
  while (k * d < n) {
    const anio = k * d
    const costeActualizado = C * Math.pow(1 + pConst, anio)
    van += costeActualizado / Math.pow(1 + r, anio)
    k++
  }
  return van
}

// ─── Valores por defecto ──────────────────────────────────────────────────────
const DEFAULT_CONFIG = {
  nombre: '',
  gfa: '',
  periodo: 60,
  tasaDescuento: 3.0,      // % capital
  inflacionConst: 2.0,     // % costes construcción/mantenimiento
  inflacionEnergia: 5.0,   // % energía
  inflacionAgua: 2.0,      // % agua
  inflacionServicios: 2.0, // % limpieza/gestión
}

const DEFAULT_TECNICO = (id, label = '') => ({
  id,
  descripcion: label,
  coste: '',
  vidaUtil: 25,
})

const DEFAULT_SUMINISTRO = (id, label = '', unidad = 'kWh/a') => ({
  id, descripcion: label, cantidad: '', precioUnitario: '', unidad,
})

const DEFAULT_LIMPIEZA = (id, label = '') => ({
  id, descripcion: label, superficie: '', horas_m2_año: '', coste_hora: '',
})

const TECNICOS_INICIALES = [
  { id: 1, descripcion: 'Saneamiento y fontanería', coste: '', vidaUtil: 25 },
  { id: 2, descripcion: 'Calefacción / ACS',        coste: '', vidaUtil: 25 },
  { id: 3, descripcion: 'Ventilación / Climatización', coste: '', vidaUtil: 25 },
  { id: 4, descripcion: 'Instalación eléctrica',    coste: '', vidaUtil: 25 },
  { id: 5, descripcion: 'Telecomunicaciones',       coste: '', vidaUtil: 25 },
  { id: 6, descripcion: 'Ascensores / Transporte',  coste: '', vidaUtil: 25 },
  { id: 7, descripcion: 'Domótica / BMS',           coste: '', vidaUtil: 25 },
  { id: 8, descripcion: 'Protección contra incendios', coste: '', vidaUtil: 25 },
]

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  wrap: { padding: '24px', fontFamily: 'inherit' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 700, color: '#2d3748' },
  subtitle: { fontSize: 13, color: '#718096', marginTop: 2 },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '8px 14px', fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? '#2563eb' : '#718096', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    cursor: 'pointer', whiteSpace: 'nowrap',
  }),
  card: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 14 },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  grid4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 },
  label: { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  inputSm: { width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' },
  btnAdd: { display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginTop: 8 },
  btnDel: { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 6px', fontSize: 14 },
  btnSave: (saving) => ({ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: saving ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { background: '#f1f5f9', padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  resultBox: (color) => ({ background: color + '15', border: `1px solid ${color}40`, borderRadius: 8, padding: 16, textAlign: 'center' }),
  resultVal: { fontSize: 22, fontWeight: 700, color: '#1e293b' },
  resultLabel: { fontSize: 12, color: '#64748b', marginTop: 2 },
  badgeOk: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeFail: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  note: { fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginTop: 6 },
  compRow: (mejor) => ({ background: mejor ? '#f0fdf4' : '#fff', border: `1px solid ${mejor ? '#86efac' : '#e2e8f0'}`, borderRadius: 8, padding: 14, flex: 1 }),
}

const fmt = (n, decimals = 0) => isNaN(n) ? '—' : n.toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

// ─── Componente principal ─────────────────────────────────────────────────────
export default function GST5Calculator({ projectId, onSave, saving }) {
  const [tab, setTab] = useState('config')
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [estructural, setEstructural] = useState('')
  const [tecnicos, setTecnicos] = useState(TECNICOS_INICIALES)
  const [suministros, setSuministros] = useState([
    DEFAULT_SUMINISTRO(1, 'Agua potable', 'm³/a'),
    DEFAULT_SUMINISTRO(2, 'Gas natural', 'kWh/a'),
    DEFAULT_SUMINISTRO(3, 'Electricidad red', 'kWh/a'),
  ])
  const [limpieza, setLimpieza] = useState([
    DEFAULT_LIMPIEZA(1, 'Suelos interiores'),
    DEFAULT_LIMPIEZA(2, 'Vidrios / fachada'),
    DEFAULT_LIMPIEZA(3, 'Zonas sanitarias'),
  ])
  const [comparativa, setComparativa] = useState([
    { id: 1, componente: '', opcionA: '', costeA: '', vidaA: 25, opcionB: '', costeB: '', vidaB: 30 },
  ])
  const [loaded, setLoaded] = useState(false)
  const nextIdRef = React.useRef(100)

  // ─── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || loaded) return
    loadData(projectId).then(d => {
      if (d) {
        if (d.config) setConfig(d.config)
        if (d.estructural !== undefined) setEstructural(d.estructural)
        if (d.tecnicos) setTecnicos(d.tecnicos)
        if (d.suministros) setSuministros(d.suministros)
        if (d.limpieza) setLimpieza(d.limpieza)
        if (d.comparativa) setComparativa(d.comparativa)
      }
      setLoaded(true)
    })
  }, [projectId, loaded])

  const [loadingProject, setLoadingProject] = useState(false)
  const [projectDataMsg, setProjectDataMsg] = useState(null)

  // ── Importador presupuesto ────────────────────────────────────────────────
  const [excelFiles, setExcelFiles] = useState([])          // archivos .xlsx de la biblioteca
  const [selectedExcel, setSelectedExcel] = useState('')    // storage_path seleccionado
  const [loadingExcel, setLoadingExcel] = useState(false)
  const [partidas, setPartidas] = useState([])              // filas detectadas del Excel
  const [showImportPanel, setShowImportPanel] = useState(false)

  // Cargar lista de excels de la biblioteca al montar
  useEffect(() => {
    if (!projectId) return
    fetchLibraryBudgetFiles(projectId).then(setExcelFiles)
  }, [projectId])

  const handleParseExcel = useCallback(async () => {
    if (!selectedExcel) return
    setLoadingExcel(true)
    setPartidas([])
    try {
      const fileName = excelFiles.find(f => f.storage_path === selectedExcel)?.file_name || ''
      const rows = await downloadAndParseFile(selectedExcel, fileName)
      setPartidas(rows)
    } catch (e) {
      alert('Error al leer el presupuesto: ' + e.message)
    }
    setLoadingExcel(false)
  }, [selectedExcel, excelFiles])

  // Cambiar asignación de una partida
  const setAsignacion = (idx, valor) => setPartidas(prev => prev.map((p, i) => i === idx ? { ...p, asignacion: valor } : p))

  // Aplicar partidas seleccionadas a inversión
  const aplicarPartidas = useCallback(() => {
    const estructuralTotal = partidas
      .filter(p => p.asignacion === 'estructural')
      .reduce((s, p) => s + p.importe, 0)

    const tecnicasAgrupadas = partidas.filter(p => p.asignacion === 'tecnico')

    if (estructuralTotal > 0) setEstructural(String(Math.round(estructuralTotal)))

    if (tecnicasAgrupadas.length > 0) {
      const nuevos = tecnicasAgrupadas.map((p, i) => ({
        id: Date.now() + i,
        descripcion: p.descripcion.length > 60 ? p.descripcion.slice(0, 60) + '…' : p.descripcion,
        coste: p.importe,
        vidaUtil: 25,
      }))
      setTecnicos(prev => {
        // Mantener sólo los que tienen datos manuales (coste > 0), añadir los nuevos
        const conDatos = prev.filter(t => Number(t.coste) > 0)
        return [...conDatos, ...nuevos]
      })
    }
    setShowImportPanel(false)
    setPartidas([])
    alert(`✅ Aplicado:\n• Estructural: €${Math.round(estructuralTotal).toLocaleString('es-ES')}\n• ${tecnicasAgrupadas.length} sistema(s) técnico(s)`)
  }, [partidas])

  const loadProjectData = useCallback(async () => {
    setLoadingProject(true)
    setProjectDataMsg(null)
    try {
      const d = await fetchProjectData(projectId)
      const msgs = []

      // GFA
      if (d.gfa) {
        setConfig(prev => ({ ...prev, gfa: d.gfa, nombre: d.projectName ? `${d.projectName} — CCV` : prev.nombre }))
        msgs.push(`✅ GFA: ${d.gfa.toLocaleString('es-ES')} m²`)
      } else {
        msgs.push('⚠️ GFA no encontrado — introdúcelo manualmente (requiere ENE 1 o MAT 1 guardados)')
      }

      // Energía — pre-rellena suministros con el consumo total de ENE 1
      if (d.energiaAnual) {
        setSuministros(prev => {
          // Buscar si ya hay una línea de electricidad; si no, añadir
          const yaExiste = prev.find(s => s.unidad === 'kWh/a' && s.descripcion.toLowerCase().includes('electr'))
          if (yaExiste) {
            return prev.map(s => s.id === yaExiste.id
              ? { ...s, cantidad: Math.round(d.energiaAnual), descripcion: 'Consumo energético total (ENE 1)', precioUnitario: s.precioUnitario || 0.25 }
              : s)
          }
          return [
            ...prev,
            { id: Date.now(), descripcion: 'Consumo energético total (ENE 1)', cantidad: Math.round(d.energiaAnual), precioUnitario: 0.25, unidad: 'kWh/a' },
          ]
        })
        msgs.push(`✅ Energía: ${Math.round(d.energiaAnual).toLocaleString('es-ES')} kWh/año (precio €/kWh pendiente de ajustar)`)
      } else {
        msgs.push('⚠️ Consumo energético no encontrado — comprueba que ENE 1 tiene los valores de consumo evaluado (con_eval)')
      }

      msgs.push('')
      msgs.push('📋 Pendiente de introducir manualmente:')
      msgs.push('  • Costes de inversión estructural (€)')
      msgs.push('  • Costes y vida útil de cada instalación técnica (€)')
      msgs.push('  • Consumo de agua (m³/año) — AG 1 guarda caudales, no volumen anual')
      msgs.push('  • Superficies y costes de limpieza')
      msgs.push('  • Comparativa de opciones (criterio 5 BREEAM)')

      setProjectDataMsg(msgs.join('\n'))
    } catch (e) {
      setProjectDataMsg('❌ Error al leer datos del proyecto: ' + e.message)
    }
    setLoadingProject(false)
  }, [projectId])

  const handleSave = useCallback(async () => {
    const payload = { config, estructural, tecnicos, suministros, limpieza, comparativa }
    await saveData(projectId, payload)
    const res = calcResultados()
    onSave && onSave({
      vanTotal: res.vanTotal,
      vanEspecifico: res.vanEspecifico,
      puntos: res.puntosBREEAM,
    })
    // calcResultados se declara más abajo (TDZ si se pusiera en deps).
    // Sus deps (config/estructural/tecnicos/...) ya están en las de
    // handleSave, así que la recreación es equivalente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, estructural, tecnicos, suministros, limpieza, comparativa, projectId, onSave])

  // ─── Cálculo ─────────────────────────────────────────────────────────────
  const calcResultados = useCallback(() => {
    const r  = config.tasaDescuento    / 100
    const pC = config.inflacionConst   / 100
    const pE = config.inflacionEnergia / 100
    const pA = config.inflacionAgua    / 100
    const pS = config.inflacionServicios / 100
    const n  = Number(config.periodo) || 50
    const gfa = Number(config.gfa) || 1

    // Inversión
    const invEstructural = Number(estructural) || 0
    const invTecnico = tecnicos.reduce((s, t) => s + (Number(t.coste) || 0), 0)
    const invTotal = invEstructural + invTecnico

    // Inspección/servicio: 0.1% estructural/año, 0.35% técnico/año (ratios habituales UNE-EN 15643-4 / IDAE)
    const inspeccionEstructAnual = invEstructural * 0.001
    const inspeccionTecnicoAnual = invTecnico * 0.0035
    const vanInspeccionEstructural = vanRentaCreciente(inspeccionEstructAnual, r, pC, n)
    const vanInspeccionTecnico     = vanRentaCreciente(inspeccionTecnicoAnual, r, pC, n)

    // Reparaciones: ~2% estructural/año, ~3% técnico/año (ratios habituales UNE-EN 15643-4)
    const reparEstructAnual = invEstructural * 0.005
    const reparTecnicoAnual = invTecnico * 0.015
    const vanReparEstructural = vanRentaCreciente(reparEstructAnual, r, pC, n)
    const vanReparTecnico     = vanRentaCreciente(reparTecnicoAnual, r, pC, n)

    // Sustituciones técnicas
    const vanSust = tecnicos.reduce((s, t) => {
      return s + vanSustituciones(Number(t.coste) || 0, Number(t.vidaUtil) || 25, n, r, pC)
    }, 0)

    // Suministros
    const vanSuministros = suministros.reduce((s, sm) => {
      const q = Number(sm.cantidad) || 0
      const p = Number(sm.precioUnitario) || 0
      const anual = q * p
      const inflacion = sm.unidad === 'm³/a' ? pA : pE
      return s + vanRentaCreciente(anual, r, inflacion, n)
    }, 0)

    // Limpieza
    const vanLimpieza = limpieza.reduce((s, l) => {
      const sup = Number(l.superficie) || 0
      const h   = Number(l.horas_m2_año) || 0
      const ch  = Number(l.coste_hora) || 0
      const anual = sup * h * ch
      return s + vanRentaCreciente(anual, r, pS, n)
    }, 0)

    const vanExplotacion = vanSuministros + vanLimpieza + vanInspeccionEstructural + vanInspeccionTecnico + vanReparEstructural + vanReparTecnico + vanSust
    const vanTotal = invTotal + vanExplotacion
    const vanEspecifico = gfa > 0 ? vanTotal / gfa : 0

    // Comparativa de opciones
    const compResultados = comparativa.map(c => {
      const cA = Number(c.costeA) || 0
      const cB = Number(c.costeB) || 0
      const vanA = cA + vanSustituciones(cA, Number(c.vidaA) || 25, n, r, pC)
      const vanB = cB + vanSustituciones(cB, Number(c.vidaB) || 25, n, r, pC)
      return { ...c, vanA, vanB, mejor: vanA <= vanB ? 'A' : 'B' }
    })

    // Puntos BREEAM GST 5
    const tieneCCV = invTotal > 0
    const tieneComparativa = comparativa.some(c => c.costeA && c.costeB && c.componente)
    const puntosBREEAM = tieneCCV ? (tieneComparativa ? 2 : 1) : 0

    return {
      invEstructural, invTecnico, invTotal,
      vanSuministros, vanLimpieza,
      vanInspeccionEstructural, vanInspeccionTecnico,
      vanReparEstructural, vanReparTecnico,
      vanSust, vanExplotacion, vanTotal,
      vanEspecifico, compResultados, puntosBREEAM,
    }
  }, [config, estructural, tecnicos, suministros, limpieza, comparativa])

  const res = calcResultados()

  // ─── Helpers de actualización ─────────────────────────────────────────────
  const upConfig = (k, v) => setConfig(prev => ({ ...prev, [k]: v }))

  const upTecnico = (id, k, v) => setTecnicos(prev => prev.map(t => t.id === id ? { ...t, [k]: v } : t))
  const addTecnico = () => setTecnicos(prev => [...prev, DEFAULT_TECNICO(++nextIdRef.current)])
  const delTecnico = (id) => setTecnicos(prev => prev.filter(t => t.id !== id))

  const upSuministro = (id, k, v) => setSuministros(prev => prev.map(s => s.id === id ? { ...s, [k]: v } : s))
  const addSuministro = () => setSuministros(prev => [...prev, DEFAULT_SUMINISTRO(++nextIdRef.current)])
  const delSuministro = (id) => setSuministros(prev => prev.filter(s => s.id !== id))

  const upLimpieza = (id, k, v) => setLimpieza(prev => prev.map(l => l.id === id ? { ...l, [k]: v } : l))
  const addLimpieza = () => setLimpieza(prev => [...prev, DEFAULT_LIMPIEZA(++nextIdRef.current)])
  const delLimpieza = (id) => setLimpieza(prev => prev.filter(l => l.id !== id))

  const upComp = (id, k, v) => setComparativa(prev => prev.map(c => c.id === id ? { ...c, [k]: v } : c))
  const addComp = () => setComparativa(prev => [...prev, { id: ++nextIdRef.current, componente: '', opcionA: '', costeA: '', vidaA: 25, opcionB: '', costeB: '', vidaB: 30 }])
  const delComp = (id) => setComparativa(prev => prev.filter(c => c.id !== id))

  // ─── TABS ─────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'config',     label: 'Configuración' },
    { id: 'inversion',  label: 'Inversión' },
    { id: 'explotacion',label: 'Explotación' },
    { id: 'comparativa',label: 'Comparativa opciones' },
    { id: 'resultados', label: 'Resultados CCV' },
  ]

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <ChartBar size={22} color="#2563eb" />
        <div>
          <div style={S.title}>Calculadora CCV — GST 5</div>
          <div style={S.subtitle}>Coste del Ciclo de Vida · UNE-EN 15643-4:2012 · Período {config.periodo} años</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: loadingProject ? '#f3f4f6' : '#eff6ff', color: loadingProject ? '#9ca3af' : '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: loadingProject ? 'not-allowed' : 'pointer' }}
            onClick={loadProjectData}
            disabled={loadingProject}
            title="Cargar GFA y consumo energético desde las evaluaciones ENE 1 y MAT 1 guardadas en este proyecto"
          >
            <DownloadSimple size={14} /> {loadingProject ? 'Cargando…' : 'Cargar datos del proyecto'}
          </button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Puntos BREEAM:</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: res.puntosBREEAM > 0 ? '#059669' : '#9ca3af' }}>
            {res.puntosBREEAM} / 3
          </span>
          <button style={S.btnSave(saving)} onClick={handleSave} disabled={saving}>
            <FloppyDisk size={15} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Mensaje de carga de datos del proyecto */}
      {projectDataMsg && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: '#374151', whiteSpace: 'pre-line', lineHeight: 1.8 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#1e40af' }}>Datos importados del proyecto</div>
          {projectDataMsg}
          <button style={{ marginTop: 8, fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setProjectDataMsg(null)}>✕ Cerrar</button>
        </div>
      )}

      {/* ── TAB: CONFIGURACIÓN ──────────────────────────────────────────── */}
      {tab === 'config' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}><Buildings size={16} /> Datos del proyecto</div>
            <div style={S.grid2}>
              <div>
                <label style={S.label}>Descripción del proyecto</label>
                <input style={S.input} value={config.nombre} onChange={e => upConfig('nombre', e.target.value)} placeholder="Ej. Edificio de oficinas Barcelona" />
              </div>
              <div>
                <label style={S.label}>Superficie Total Construida GFAs (m²)</label>
                <input style={S.input} type="number" value={config.gfa} onChange={e => upConfig('gfa', e.target.value)} placeholder="Ej. 10.713" />
              </div>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Parámetros del análisis</div>
            <div style={S.grid3}>
              <div>
                <label style={S.label}>Período de estudio (años) — mín. 40, ideal 60</label>
                <input style={S.input} type="number" min={40} max={100} value={config.periodo}
                  onChange={e => upConfig('periodo', Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Tasa de descuento / interés capital (%)</label>
                <input style={S.input} type="number" step={0.1} value={config.tasaDescuento}
                  onChange={e => upConfig('tasaDescuento', Number(e.target.value))} />
              </div>
              <div />
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Tasas de inflación de precios (%/año)</div>
            <div style={S.grid4}>
              <div>
                <label style={S.label}>Construcción / Mantenimiento</label>
                <input style={S.input} type="number" step={0.1} value={config.inflacionConst}
                  onChange={e => upConfig('inflacionConst', Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Energía (calor + electricidad)</label>
                <input style={S.input} type="number" step={0.1} value={config.inflacionEnergia}
                  onChange={e => upConfig('inflacionEnergia', Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Agua / Saneamiento</label>
                <input style={S.input} type="number" step={0.1} value={config.inflacionAgua}
                  onChange={e => upConfig('inflacionAgua', Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Servicios (limpieza, gestión)</label>
                <input style={S.input} type="number" step={0.1} value={config.inflacionServicios}
                  onChange={e => upConfig('inflacionServicios', Number(e.target.value))} />
              </div>
            </div>
            <p style={S.note}>Valores por defecto conformes a BREEAM ES NC 2015 · UNE-EN 15643-4:2012 y Reglamento Delegado (UE) 244/2012 para España (tasa descuento 3%, energía 5%, resto 2%)</p>
          </div>
        </div>
      )}

      {/* ── TAB: INVERSIÓN ──────────────────────────────────────────────── */}
      {tab === 'inversion' && (
        <div>

          {/* ── IMPORTADOR PRESUPUESTO ── */}
          <div style={{ ...S.card, borderColor: '#bfdbfe', background: '#eff6ff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={S.cardTitle}><FileXls size={16} color="#2563eb" /> Importar desde presupuesto (biblioteca)</div>
              <button style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setShowImportPanel(v => !v)}>
                {showImportPanel ? '▲ Ocultar' : '▼ Mostrar'}
              </button>
            </div>

            {showImportPanel && (
              <div>
                {excelFiles.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#6b7280' }}>No se encontraron archivos de presupuesto (.xlsx, .xls, .pdf) en la biblioteca de este proyecto.</p>
                ) : (
                  <div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={S.label}>Selecciona el archivo de presupuesto (.xlsx o .pdf)</label>
                        <select style={S.input} value={selectedExcel} onChange={e => { setSelectedExcel(e.target.value); setPartidas([]) }}>
                          <option value="">— Elige un archivo —</option>
                          {excelFiles.map(f => (
                            <option key={f.id} value={f.storage_path}>{f.file_name}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        style={{ ...S.btnSave(loadingExcel || !selectedExcel), padding: '7px 14px', fontSize: 12 }}
                        onClick={handleParseExcel}
                        disabled={loadingExcel || !selectedExcel}
                      >
                        {loadingExcel ? 'Leyendo…' : 'Leer archivo'}
                      </button>
                    </div>

                    {partidas.length > 0 && (
                      <div>
                        {/* Resumen de clasificación automática */}
                        {(() => {
                          const autoEst = partidas.filter(p => p.asignacion === 'estructural').length
                          const autoTec = partidas.filter(p => p.asignacion === 'tecnico').length
                          const sinClasif = partidas.filter(p => !p.asignacion).length
                          return (
                            <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                              <div style={{ padding: '6px 12px', background: '#eff6ff', borderRadius: 6, fontSize: 12 }}>
                                🏗️ <strong>{autoEst}</strong> partidas → Estructural
                              </div>
                              <div style={{ padding: '6px 12px', background: '#f0fdf4', borderRadius: 6, fontSize: 12 }}>
                                ⚙️ <strong>{autoTec}</strong> partidas → Técnico
                              </div>
                              {sinClasif > 0 && (
                                <div style={{ padding: '6px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 12, color: '#9a3412' }}>
                                  ⚠️ <strong>{sinClasif}</strong> sin clasificar — revisa manualmente
                                </div>
                              )}
                            </div>
                          )
                        })()}

                        {/* Botones de asignación rápida para correcciones */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: '#6b7280', alignSelf: 'center' }}>Reasignar todo:</span>
                          <button style={{ ...S.btnAdd, margin: 0 }} onClick={() => setPartidas(p => p.map(r => ({ ...r, asignacion: 'estructural' })))}>→ Estructural</button>
                          <button style={{ ...S.btnAdd, margin: 0 }} onClick={() => setPartidas(p => p.map(r => ({ ...r, asignacion: 'tecnico' })))}>→ Técnico</button>
                          <button style={{ ...S.btnAdd, margin: 0 }} onClick={() => setPartidas(p => p.map(r => ({ ...r, asignacion: '' })))}>Limpiar</button>
                        </div>

                        <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                          <table style={S.table}>
                            <thead style={{ position: 'sticky', top: 0 }}>
                              <tr>
                                <th style={S.th}>Descripción partida</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>Importe (€)</th>
                                <th style={S.th}>Asignar a</th>
                              </tr>
                            </thead>
                            <tbody>
                              {partidas.map((p, idx) => (
                                <tr key={idx} style={{ background: p.asignacion === 'estructural' ? '#eff6ff' : p.asignacion === 'tecnico' ? '#f0fdf4' : '#fffbeb', borderLeft: !p.asignacion ? '3px solid #f59e0b' : 'none' }}>
                                  <td style={{ ...S.td, fontSize: 11 }}>
                                    <span style={{ color: '#9ca3af', marginRight: 4 }}>[{p.hoja}]</span>
                                    {p.descripcion}
                                  </td>
                                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, fontSize: 12 }}>
                                    € {p.importe.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td style={S.td}>
                                    <select
                                      style={{ ...S.inputSm, minWidth: 120 }}
                                      value={p.asignacion}
                                      onChange={e => setAsignacion(idx, e.target.value)}
                                    >
                                      <option value="">— Ignorar —</option>
                                      <option value="estructural">Estructural</option>
                                      <option value="tecnico">Técnico (instalación)</option>
                                    </select>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Resumen de asignación */}
                        <div style={{ display: 'flex', gap: 16, marginTop: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>Estructural: <strong>€ {partidas.filter(p => p.asignacion === 'estructural').reduce((s, p) => s + p.importe, 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })}</strong></span>
                          <span>Técnico: <strong>€ {partidas.filter(p => p.asignacion === 'tecnico').reduce((s, p) => s + p.importe, 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })}</strong> ({partidas.filter(p => p.asignacion === 'tecnico').length} líneas)</span>
                          <button
                            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                            onClick={aplicarPartidas}
                            disabled={!partidas.some(p => p.asignacion)}
                          >
                            <ArrowRight size={13} /> Aplicar a inversión
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}><Buildings size={16} /> Elementos estructurales</div>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
              Coste total de la construcción estructural (cimentación, estructura, cerramientos, acabados). Corresponde a las partidas no sustituibles dentro del período de análisis.
            </p>
            <div style={{ maxWidth: 320 }}>
              <label style={S.label}>Coste total elementos estructurales (€)</label>
              <input style={S.input} type="number" value={estructural}
                onChange={e => setEstructural(e.target.value)} placeholder="Ej. 7.054.221" />
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}><Wrench size={16} /> Elementos técnicos (instalaciones)</div>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Introduce el coste y la vida útil de cada sistema. Las sustituciones dentro del período de análisis se calculan automáticamente.
            </p>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Sistema / Instalación</th>
                  <th style={S.th}>Coste inversión (€)</th>
                  <th style={S.th}>Vida útil (años)</th>
                  <th style={S.th}>Sustituciones en {config.periodo}a</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {tecnicos.map(t => {
                  const d = Number(t.vidaUtil) || 25
                  const n = Number(config.periodo) || 50
                  const sust = Math.max(0, Math.floor(n / d) - (n % d === 0 ? 1 : 0))
                  return (
                    <tr key={t.id}>
                      <td style={S.td}>
                        <input style={S.inputSm} value={t.descripcion}
                          onChange={e => upTecnico(t.id, 'descripcion', e.target.value)} />
                      </td>
                      <td style={S.td}>
                        <input style={S.inputSm} type="number" value={t.coste}
                          onChange={e => upTecnico(t.id, 'coste', e.target.value)} placeholder="0" />
                      </td>
                      <td style={S.td}>
                        <input style={{ ...S.inputSm, maxWidth: 70 }} type="number" value={t.vidaUtil}
                          onChange={e => upTecnico(t.id, 'vidaUtil', e.target.value)} />
                      </td>
                      <td style={{ ...S.td, textAlign: 'center', color: sust > 0 ? '#d97706' : '#6b7280' }}>
                        {sust > 0 ? `${sust} ${sust > 1 ? 'veces' : 'vez'}` : 'Ninguna'}
                      </td>
                      <td style={S.td}>
                        <button style={S.btnDel} onClick={() => delTecnico(t.id)}>✕</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <button style={S.btnAdd} onClick={addTecnico}><Plus size={13} /> Añadir sistema</button>

            <div style={{ marginTop: 14, display: 'flex', gap: 20, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 13 }}><span style={{ color: '#6b7280' }}>Total técnico: </span><strong>€ {fmt(tecnicos.reduce((s, t) => s + (Number(t.coste) || 0), 0))}</strong></div>
              <div style={{ fontSize: 13 }}><span style={{ color: '#6b7280' }}>Total estructural: </span><strong>€ {fmt(Number(estructural) || 0)}</strong></div>
              <div style={{ fontSize: 13 }}><span style={{ color: '#2563eb' }}>Inversión total: </span><strong style={{ color: '#2563eb' }}>€ {fmt(res.invTotal)}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: EXPLOTACIÓN ────────────────────────────────────────────── */}
      {tab === 'explotacion' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}><Lightning size={16} /> Suministros (energía y agua)</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Suministro</th>
                  <th style={S.th}>Unidad</th>
                  <th style={S.th}>Consumo anual</th>
                  <th style={S.th}>Precio unitario (€/unidad)</th>
                  <th style={S.th}>Coste anual (€/a)</th>
                  <th style={S.th}>VAN {config.periodo}a (€)</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {suministros.map(sm => {
                  const r  = config.tasaDescuento / 100
                  const inflacion = sm.unidad === 'm³/a' ? config.inflacionAgua / 100 : config.inflacionEnergia / 100
                  const anual = (Number(sm.cantidad) || 0) * (Number(sm.precioUnitario) || 0)
                  const van = vanRentaCreciente(anual, r, inflacion, Number(config.periodo) || 50)
                  return (
                    <tr key={sm.id}>
                      <td style={S.td}><input style={S.inputSm} value={sm.descripcion} onChange={e => upSuministro(sm.id, 'descripcion', e.target.value)} /></td>
                      <td style={S.td}>
                        <select style={S.inputSm} value={sm.unidad} onChange={e => upSuministro(sm.id, 'unidad', e.target.value)}>
                          <option value="kWh/a">kWh/a</option>
                          <option value="m³/a">m³/a</option>
                        </select>
                      </td>
                      <td style={S.td}><input style={S.inputSm} type="number" value={sm.cantidad} onChange={e => upSuministro(sm.id, 'cantidad', e.target.value)} placeholder="0" /></td>
                      <td style={S.td}><input style={S.inputSm} type="number" step="0.001" value={sm.precioUnitario} onChange={e => upSuministro(sm.id, 'precioUnitario', e.target.value)} placeholder="0.00" /></td>
                      <td style={{ ...S.td, color: '#374151' }}>€ {fmt(anual)}</td>
                      <td style={{ ...S.td, color: '#2563eb', fontWeight: 600 }}>€ {fmt(van)}</td>
                      <td style={S.td}><button style={S.btnDel} onClick={() => delSuministro(sm.id)}>✕</button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <button style={S.btnAdd} onClick={addSuministro}><Plus size={13} /> Añadir suministro</button>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}><Broom size={16} /> Limpieza</div>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
              Si usas la tarifa estándar hotelera (€/m²·a todo incluido), introduce <strong>h/m²·año = 1</strong> y <strong>€/hora = tarifa</strong>. Si usas coste horario real, introduce las horas y el coste por separado.
            </p>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Zona / Tipo de superficie</th>
                  <th style={S.th}>Superficie (m²)</th>
                  <th style={S.th}>h/m²·año (o 1 si tarifa all-in)</th>
                  <th style={S.th}>€/hora (o tarifa €/m²·a)</th>
                  <th style={S.th}>Coste anual (€/a)</th>
                  <th style={S.th}>VAN {config.periodo}a (€)</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {limpieza.map(l => {
                  const r  = config.tasaDescuento / 100
                  const pS = config.inflacionServicios / 100
                  const anual = (Number(l.superficie) || 0) * (Number(l.horas_m2_año) || 0) * (Number(l.coste_hora) || 0)
                  const van = vanRentaCreciente(anual, r, pS, Number(config.periodo) || 50)
                  return (
                    <tr key={l.id}>
                      <td style={S.td}><input style={S.inputSm} value={l.descripcion} onChange={e => upLimpieza(l.id, 'descripcion', e.target.value)} /></td>
                      <td style={S.td}><input style={S.inputSm} type="number" value={l.superficie} onChange={e => upLimpieza(l.id, 'superficie', e.target.value)} placeholder="m²" /></td>
                      <td style={S.td}><input style={S.inputSm} type="number" step="0.01" value={l.horas_m2_año} onChange={e => upLimpieza(l.id, 'horas_m2_año', e.target.value)} placeholder="h/m²·a" /></td>
                      <td style={S.td}><input style={S.inputSm} type="number" step="0.5" value={l.coste_hora} onChange={e => upLimpieza(l.id, 'coste_hora', e.target.value)} placeholder="€/h" /></td>
                      <td style={{ ...S.td, color: '#374151' }}>€ {fmt(anual)}</td>
                      <td style={{ ...S.td, color: '#2563eb', fontWeight: 600 }}>€ {fmt(van)}</td>
                      <td style={S.td}><button style={S.btnDel} onClick={() => delLimpieza(l.id)}>✕</button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <button style={S.btnAdd} onClick={addLimpieza}><Plus size={13} /> Añadir zona</button>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}><Wrench size={16} /> Inspección, mantenimiento y reparaciones (calculado automáticamente)</div>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
              Tasas aplicadas conforme a UNE-EN 15643-4:2012 (BREEAM GST 5) con ratios habituales del sector: inspección estructural 0,1%/a, inspección técnica 0,35%/a, reparación estructural 0,5%/a, reparación técnica 1,5%/a sobre los costes de inversión.
            </p>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Partida</th>
                  <th style={S.th}>Base de cálculo</th>
                  <th style={S.th}>Tasa anual</th>
                  <th style={S.th}>Coste anual (€/a)</th>
                  <th style={S.th}>VAN {config.periodo}a (€)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Inspección / servicio — Elementos estructurales', base: res.invEstructural, tasa: '0,10%', anual: res.invEstructural * 0.001, van: res.vanInspeccionEstructural },
                  { label: 'Inspección / servicio — Elementos técnicos',      base: res.invTecnico,    tasa: '0,35%', anual: res.invTecnico * 0.0035,    van: res.vanInspeccionTecnico },
                  { label: 'Reparaciones — Elementos estructurales',          base: res.invEstructural, tasa: '0,50%', anual: res.invEstructural * 0.005,  van: res.vanReparEstructural },
                  { label: 'Reparaciones — Elementos técnicos',              base: res.invTecnico,    tasa: '1,50%', anual: res.invTecnico * 0.015,      van: res.vanReparTecnico },
                ].map((row, i) => (
                  <tr key={i}>
                    <td style={S.td}>{row.label}</td>
                    <td style={{ ...S.td, color: '#6b7280' }}>€ {fmt(row.base)}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{row.tasa}</td>
                    <td style={S.td}>€ {fmt(row.anual)}</td>
                    <td style={{ ...S.td, color: '#2563eb', fontWeight: 600 }}>€ {fmt(row.van)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...S.td, fontWeight: 600 }}>VAN sustituciones técnicas</td>
                  <td style={S.td} colSpan={3}><span style={{ fontSize: 11, color: '#6b7280' }}>Calculado por sistema según vida útil</span></td>
                  <td style={{ ...S.td, color: '#2563eb', fontWeight: 600 }}>€ {fmt(res.vanSust)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB: COMPARATIVA DE OPCIONES ────────────────────────────────── */}
      {tab === 'comparativa' && (
        <div>
          <div style={{ ...S.card, background: '#fffbeb', borderColor: '#fcd34d' }}>
            <div style={{ fontSize: 13, color: '#92400e' }}>
              <strong>BREEAM GST 5 — Criterio 5:</strong> Para obtener 2 puntos, el CCV debe demostrar que los elementos de al menos 2 componentes del edificio han sido analizados comparando opciones alternativas (cerramiento, instalaciones, acabados, espacios exteriores o estructuras).
            </div>
          </div>

          {comparativa.map((c, idx) => {
            const cr = res.compResultados.find(r => r.id === c.id) || {}
            return (
              <div key={c.id} style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={S.cardTitle}>Componente {idx + 1}</div>
                  {comparativa.length > 1 && <button style={S.btnDel} onClick={() => delComp(c.id)}>✕ Eliminar</button>}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={S.label}>Tipo de componente analizado</label>
                  <select style={{ ...S.input, maxWidth: 340 }} value={c.componente} onChange={e => upComp(c.id, 'componente', e.target.value)}>
                    <option value="">— Selecciona —</option>
                    <option value="cerramiento">Cerramiento (ventanas, cubierta, revestimiento)</option>
                    <option value="instalaciones">Instalaciones (calefacción, AC, control)</option>
                    <option value="acabados">Acabados (muros, suelos, techos)</option>
                    <option value="espacios_externos">Espacios externos (urbanización, cierres)</option>
                    <option value="estructuras">Estructuras</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  {/* Opción A */}
                  <div style={S.compRow(cr.mejor === 'A')}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 10 }}>
                      Opción A {cr.mejor === 'A' && <span style={S.badgeOk}><CheckCircle size={11} /> Opción preferida</span>}
                    </div>
                    <label style={S.label}>Descripción de la opción A</label>
                    <input style={S.input} value={c.opcionA} onChange={e => upComp(c.id, 'opcionA', e.target.value)} placeholder="Ej. Fachada ventilada aluminio" />
                    <div style={S.grid2}>
                      <div>
                        <label style={{ ...S.label, marginTop: 8 }}>Coste de inversión (€)</label>
                        <input style={S.input} type="number" value={c.costeA} onChange={e => upComp(c.id, 'costeA', e.target.value)} placeholder="0" />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginTop: 8 }}>Vida útil (años)</label>
                        <input style={S.input} type="number" value={c.vidaA} onChange={e => upComp(c.id, 'vidaA', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 6 }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>VAN total (inversión + sustituciones)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>€ {fmt(cr.vanA)}</div>
                    </div>
                  </div>

                  {/* Opción B */}
                  <div style={S.compRow(cr.mejor === 'B')}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46', marginBottom: 10 }}>
                      Opción B {cr.mejor === 'B' && <span style={S.badgeOk}><CheckCircle size={11} /> Opción preferida</span>}
                    </div>
                    <label style={S.label}>Descripción de la opción B</label>
                    <input style={S.input} value={c.opcionB} onChange={e => upComp(c.id, 'opcionB', e.target.value)} placeholder="Ej. Fachada GRC prefabricado" />
                    <div style={S.grid2}>
                      <div>
                        <label style={{ ...S.label, marginTop: 8 }}>Coste de inversión (€)</label>
                        <input style={S.input} type="number" value={c.costeB} onChange={e => upComp(c.id, 'costeB', e.target.value)} placeholder="0" />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginTop: 8 }}>Vida útil (años)</label>
                        <input style={S.input} type="number" value={c.vidaB} onChange={e => upComp(c.id, 'vidaB', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6 }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>VAN total (inversión + sustituciones)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#065f46' }}>€ {fmt(cr.vanB)}</div>
                    </div>
                  </div>
                </div>

                {cr.vanA > 0 && cr.vanB > 0 && (
                  <div style={{ marginTop: 10, padding: '8px 14px', background: '#f8fafc', borderRadius: 6, fontSize: 12, color: '#374151' }}>
                    Diferencia de VAN: <strong>€ {fmt(Math.abs(cr.vanA - cr.vanB))}</strong> ({((Math.abs(cr.vanA - cr.vanB) / Math.max(cr.vanA, cr.vanB)) * 100).toFixed(1)}%) · Se recomienda <strong>Opción {cr.mejor}</strong>
                  </div>
                )}
              </div>
            )
          })}

          <button style={S.btnAdd} onClick={addComp}><Plus size={13} /> Añadir comparativa de componente</button>

          {comparativa.filter(c => c.componente && c.costeA && c.costeB).length < 2 && (
            <div style={{ ...S.card, marginTop: 14, background: '#fff7ed', borderColor: '#fed7aa' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#9a3412' }}>
                <Warning size={16} />
                Para alcanzar 2 puntos BREEAM es necesario comparar opciones de al menos 2 componentes distintos.
                Actualmente: {comparativa.filter(c => c.componente && c.costeA && c.costeB).length} / 2 componentes analizados.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: RESULTADOS ─────────────────────────────────────────────── */}
      {tab === 'resultados' && (
        <div>
          {/* KPIs principales */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div style={S.resultBox('#2563eb')}>
              <div style={S.resultVal}>€ {fmt(res.invTotal)}</div>
              <div style={S.resultLabel}>Coste de inversión total</div>
            </div>
            <div style={S.resultBox('#7c3aed')}>
              <div style={S.resultVal}>€ {fmt(res.vanExplotacion)}</div>
              <div style={S.resultLabel}>VAN costes explotación ({config.periodo}a)</div>
            </div>
            <div style={S.resultBox('#0891b2')}>
              <div style={S.resultVal}>€ {fmt(res.vanTotal)}</div>
              <div style={S.resultLabel}>VAN total ciclo de vida</div>
            </div>
            <div style={S.resultBox('#059669')}>
              <div style={S.resultVal}>€ {fmt(res.vanEspecifico, 0)}/m²</div>
              <div style={S.resultLabel}>VAN específico por m² GFAs</div>
            </div>
          </div>

          {/* Tabla desglose */}
          <div style={S.card}>
            <div style={S.cardTitle}>Desglose del coste del ciclo de vida</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Categoría de coste</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Importe (€)</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>% del total</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Inversión — Elementos estructurales',       val: res.invEstructural,           group: 'inv' },
                  { label: 'Inversión — Elementos técnicos',            val: res.invTecnico,               group: 'inv' },
                  { label: 'Explotación — Suministros (agua + energía)',val: res.vanSuministros,           group: 'exp' },
                  { label: 'Explotación — Limpieza',                    val: res.vanLimpieza,              group: 'exp' },
                  { label: 'Mantenimiento — Inspección estructural',    val: res.vanInspeccionEstructural, group: 'mnt' },
                  { label: 'Mantenimiento — Inspección técnica',        val: res.vanInspeccionTecnico,     group: 'mnt' },
                  { label: 'Mantenimiento — Reparaciones estructurales',val: res.vanReparEstructural,      group: 'mnt' },
                  { label: 'Mantenimiento — Reparaciones técnicas',     val: res.vanReparTecnico,          group: 'mnt' },
                  { label: 'Mantenimiento — Sustituciones técnicas',    val: res.vanSust,                  group: 'mnt' },
                ].map((row, i) => {
                  const pct = res.vanTotal > 0 ? (row.val / res.vanTotal * 100).toFixed(1) : '0.0'
                  const colors = { inv: '#2563eb', exp: '#7c3aed', mnt: '#0891b2' }
                  return (
                    <tr key={i}>
                      <td style={S.td}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: colors[row.group], marginRight: 6 }} />
                        {row.label}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>€ {fmt(row.val)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#6b7280' }}>{pct}%</td>
                    </tr>
                  )
                })}
                <tr style={{ background: '#f1f5f9' }}>
                  <td style={{ ...S.td, fontWeight: 700 }}>TOTAL VAN ciclo de vida completo</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>€ {fmt(res.vanTotal)}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>100%</td>
                </tr>
              </tbody>
            </table>
            <p style={S.note}>
              VAN calculado con tasa de descuento {config.tasaDescuento}% · período {config.periodo} años · UNE-EN 15643-4:2012 · Valores en euros corrientes actualizados
            </p>
          </div>

          {/* Cumplimiento BREEAM */}
          <div style={S.card}>
            <div style={S.cardTitle}>Cumplimiento BREEAM ES NC 2015 — GST 5</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Criterio</th>
                  <th style={S.th}>Requisito</th>
                  <th style={S.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    label: 'Criterio 1-2 (1 pt)', pts: '1 pto',
                    req: 'CCV realizado conforme UNE-EN 15643-4:2012, período ≥40 años, fases construcción + funcionamiento + mantenimiento',
                    ok: res.invTotal > 0 && Number(config.periodo) >= 40,
                  },
                  {
                    label: 'Criterio 3 (1 pt)', pts: '1 pto',
                    req: 'Valoración crítica de la vida útil e implicaciones de mantenimiento de los sistemas especificados',
                    ok: tecnicos.some(t => t.coste && t.vidaUtil),
                  },
                  {
                    label: 'Criterio 5-7 (2 pts)', pts: '2 pts',
                    req: 'Análisis comparativo de ≥2 componentes del edificio con opciones alternativas y preferencia al CCV más bajo',
                    ok: comparativa.filter(c => c.componente && c.costeA && c.costeB).length >= 2,
                  },
                  {
                    label: 'Criterio 9-11 (3 pts)', pts: '3 pts',
                    req: 'CCV actualizado en ejecución/construcción + aplicado en especificaciones + estrategia de mantenimiento desarrollada',
                    ok: false,
                    note: 'Requiere documentación adicional en fase de ejecución',
                  },
                ].map((row, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{row.req}{row.note && <span style={{ color: '#d97706', marginLeft: 4 }}>· {row.note}</span>}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      {row.ok
                        ? <span style={S.badgeOk}><CheckCircle size={12} /> Cumple</span>
                        : <span style={S.badgeFail}><Warning size={12} /> Pendiente</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', background: res.puntosBREEAM > 0 ? '#f0fdf4' : '#f9fafb', borderRadius: 8, border: `1px solid ${res.puntosBREEAM > 0 ? '#86efac' : '#e5e7eb'}` }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Puntuación estimada GST 5</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: res.puntosBREEAM > 0 ? '#059669' : '#9ca3af' }}>{res.puntosBREEAM} <span style={{ fontSize: 14, fontWeight: 400, color: '#6b7280' }}>/ 3 puntos</span></div>
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                {res.puntosBREEAM === 0 && 'Introduce los costes de inversión para comenzar el análisis.'}
                {res.puntosBREEAM === 1 && 'CCV básico completado (criterios 1-3). Añade comparativas de ≥2 componentes para alcanzar 2 puntos.'}
                {res.puntosBREEAM === 2 && 'CCV con análisis comparativo completado (criterios 1-7). Para 3 puntos actualiza el CCV en fase de ejecución y desarrolla la estrategia de mantenimiento.'}
                {res.puntosBREEAM === 3 && 'Criterios 1-11 cumplidos. Máxima puntuación GST 5 alcanzada.'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
